/**
 * The ResourceQuota of one environment's namespace: what the namespace may
 * hold at most, computed from the environment's configuration at its
 * MAXIMUM, plus a margin — platform-catalog `contracts/README.md`, « Quota ».
 * ONE module for both writers of `<env>/quota.yaml` in the appenv
 * repository: `waadoo:env:quota` at the project's creation (the skeleton
 * writes the manifest it is given), and `waadoo:env:configure`, in the SAME
 * commit as `<env>/values.yaml`. This file's name is the one the manifests
 * cite (« calcul : environmentQuota.ts de Backstage »).
 *
 * WHY A QUOTA: the chart's `deploy/values.yaml` lives in the PROJECT's
 * repository, which its editors push to. The quota caps what the namespace
 * may run whatever that repository says, and the editors cannot remove it:
 * the AppProject of the environment does not whitelist ResourceQuota nor
 * LimitRange (skeleton `argocd-appproject.yaml`).
 *
 * FROM THE DESCRIPTOR: the rows, in `spec.quota.order` — a component when
 * `enabled`, an extra when its `scope` covers the profile and its `when`
 * holds —, each with its pods at worst, its init containers and its `why`;
 * the extras' resources (`resourcesFrom` in the values, else `resources`);
 * the volumes; the light version of the ephemeral branches.
 *
 * FROM THE ENGINE, the platform's constants, the same for every type:
 *  - the LimitRange's defaults (`CONTAINER_DEFAULTS`) for a container that
 *    declares no resources, or a key it leaves out;
 *  - a pod, per resource: the larger of the sum of its containers and of its
 *    largest init container (`podOf`), as the scheduler counts it;
 *  - the workflows' row, a permanent environment only, added last, from
 *    the features the environment has (`WORKFLOW_RUNS`, `featureEnabled`:
 *    declared, and their `when` true over its values): `backups`, a backup
 *    runs its database and file pods in parallel (2), a restore its two
 *    restore pods after a safety backup (2), a listing one; `uploads`, an
 *    upload one — 6 pods at worst with both, no row with neither;
 *  - `QUOTA_MARGIN_PERCENT` on every figure, CPU rounded up to 100 m,
 *    memory to 128 Mi;
 *  - the cluster's ceiling (`quotaProblems`) and the manifest's text
 *    (`renderQuotaManifest`).
 *
 * NOT A RESERVATION: a quota only refuses the creation of a pod that would
 * exceed it. The requests, the scarce resource of the cluster, are what it
 * really caps; the limits are capped loosely (the workflows' defaults).
 */

import { parseDocument } from 'yaml';
import {
  CLUSTER_MAX_NODES,
  CLUSTER_REQUESTS_MAX,
  type Resources,
} from './cluster';
import { featureEnabled } from './conditions';
import {
  RESOURCE_KEYS,
  type AppTypeDescriptor,
  type DescriptorComponent,
  type DescriptorQuotaExtra,
  type FieldValues,
  type InitContainer,
  type QuotaRowSpec,
  type SidecarContainer,
  valueOf,
} from './descriptor';
import {
  evaluate,
  hasOwn,
  renderTemplate,
  truthy,
  type ExpressionNode,
} from './expression';
import {
  containerOf,
  quantityIn,
  record,
  type ContainerResources,
  type QuotaInputs,
} from './values';

export interface PodResources {
  requests: Resources;
  limits: Resources;
}

/**
 * The LimitRange of every environment (skeleton `base/limitrange.yaml`,
 * platform-catalog fork): what a container that declares no resources gets.
 * Change both together.
 *  - requests 50 m / 64 Mi: Argo's `wait` uses ~20 Mi (its `init` peaked at
 *    95 Mi), Odoo's `wait-*` init containers run a `psql` loop — a request
 *    is not a ceiling, and these containers declare nothing today;
 *  - limits 1 CPU / 2 Gi: high enough that a container declaring a request
 *    WITHOUT a limit is not refused in any plausible case. The exception:
 *    the default limit is set, then the API refuses a request above it — a
 *    container asking more than 1 CPU or 2 Gi must declare its limit.
 */
export const CONTAINER_DEFAULTS: PodResources = {
  requests: { cpu: 50, memory: 64 },
  limits: { cpu: 1000, memory: 2048 },
};

/**
 * The `main` container of a data step of the workflows (backup, restore,
 * upload), as the ClusterWorkflowTemplates of appbackuptos3 and
 * uploads3toapp declare it (branches `feat/ressources-workflows`, to be
 * published): working set measured up to 569 Mi on a database backup
 * (VictoriaMetrics, 30 days to 2026-10-16, page cache included), 1 Gi by
 * prudence, 2 CPU not to slow pg_dump, zstd or tar down. The lighter steps
 * declare less. Change both together.
 */
export const WORKFLOW_MAIN: PodResources = {
  requests: { cpu: 100, memory: 256 },
  limits: { cpu: 2000, memory: 1024 },
};

const maxOf = (a: Resources, b: Resources): Resources => ({
  cpu: Math.max(a.cpu, b.cpu),
  memory: Math.max(a.memory, b.memory),
});

/**
 * What the quota counts for a workflow's `main`: the larger of its own
 * resources and the LimitRange's defaults, so that it holds before and
 * after the templates declare them.
 */
export const WORKFLOW_MAIN_COUNTED: PodResources = {
  requests: maxOf(WORKFLOW_MAIN.requests, CONTAINER_DEFAULTS.requests),
  limits: maxOf(WORKFLOW_MAIN.limits, CONTAINER_DEFAULTS.limits),
};

/**
 * The workflow runs a permanent environment may hold at once (header), each
 * counted only when the AppType declares the feature it serves: `backups`,
 * the Backups card (`backup-cron`, `backup-restore`, `backup-list`);
 * `uploads`, the Upload card (`file-upload`). A type declaring neither (the
 * `vitrine` demo: `costs` only) has no workflows' row. In the order of the
 * row's `why`, the one of Odoo's manifests.
 */
export const WORKFLOW_RUNS: ReadonlyArray<{
  feature: 'backups' | 'uploads';
  /** In French, for the row's `why`. */
  label: string;
  pods: number;
}> = [
  { feature: 'backups', label: 'sauvegarde', pods: 2 },
  { feature: 'backups', label: 'restauration', pods: 2 },
  { feature: 'uploads', label: 'envoi', pods: 1 },
  { feature: 'backups', label: 'liste', pods: 1 },
];

/** Added to every figure of the quota. */
export const QUOTA_MARGIN_PERCENT = 20;

/** The file, in each environment's folder and in `ephemeral-template/`. */
export const QUOTA_FILE = 'quota.yaml';
/** The folder of the ephemeral branches' resources. */
export const EPHEMERAL_TEMPLATE_FOLDER = 'ephemeral-template';
/** The ResourceQuota's name, in every namespace. */
export const QUOTA_NAME = 'environment-quota';

/**
 * `permanent`: an environment of the appenv; `ephemeral`: a `dev-…` branch,
 * dev's configuration under the light version, without workflows.
 */
export type QuotaProfile = 'permanent' | 'ephemeral';

/** What the quota is computed from. */
export interface QuotaInput {
  /** The configurable fields' values. */
  configuration: FieldValues;
  /** The non-configurable fields and the extras' resources (`values.ts`). */
  inputs: QuotaInputs;
}

export interface QuotaComponent {
  /** In French, for the manifest's comment. */
  label: string;
  pods: number;
  /** One pod, as the scheduler counts it. */
  pod: PodResources;
  /** Why this count, in French. */
  why: string;
}

/** What the namespace holds at most (`total`) and the quota (`hard`). */
export interface EnvironmentQuota {
  profile: QuotaProfile;
  components: QuotaComponent[];
  total: {
    pods: number;
    requests: Resources;
    limits: Resources;
    persistentVolumeClaims: number;
    /** Gi. */
    storage: number;
  };
  hard: QuotaHard;
}

/** The figures of a ResourceQuota's `spec.hard`. */
export interface QuotaHard {
  pods: number;
  requests: Resources;
  limits: Resources;
  persistentVolumeClaims: number;
  storage: number;
}

const toPod = (container: ContainerResources): PodResources => ({
  requests: { cpu: container.cpuRequest, memory: container.memoryRequest },
  limits: { cpu: container.cpuLimit, memory: container.memoryLimit },
});

const DEFAULT_CONTAINER: ContainerResources = {
  cpuRequest: CONTAINER_DEFAULTS.requests.cpu,
  memoryRequest: CONTAINER_DEFAULTS.requests.memory,
  cpuLimit: CONTAINER_DEFAULTS.limits.cpu,
  memoryLimit: CONTAINER_DEFAULTS.limits.memory,
};

/**
 * One pod whose regular containers are `containers` and whose init
 * containers are `inits`, the containers without resources (`undefined`)
 * taking `CONTAINER_DEFAULTS`: the scheduler's sum, the largest init
 * container if larger, per resource.
 */
export function podOf(
  containers: Array<PodResources | undefined>,
  inits: Array<PodResources | undefined> = [],
): PodResources {
  const filled = (list: Array<PodResources | undefined>) =>
    list.map(item => item ?? CONTAINER_DEFAULTS);
  const regular = filled(containers);
  const init = filled(inits);
  const figure = (side: keyof PodResources, resource: keyof Resources) =>
    Math.max(
      regular.reduce((total, item) => total + item[side][resource], 0),
      ...init.map(item => item[side][resource]),
    );
  return {
    requests: {
      cpu: figure('requests', 'cpu'),
      memory: figure('requests', 'memory'),
    },
    limits: {
      cpu: figure('limits', 'cpu'),
      memory: figure('limits', 'memory'),
    },
  };
}

const withMargin = (value: number, step: number) =>
  Math.ceil(Math.ceil((value * (100 + QUOTA_MARGIN_PERCENT)) / 100) / step) *
  step;

/** A whole number ≥ 0 from an expression, or a French error. */
function count(
  node: ExpressionNode,
  values: FieldValues,
  where: string,
): number {
  const value = evaluate(node, { values });
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    throw new Error(
      `Quota : ${where} = ${JSON.stringify(value)}, attendu un entier positif.`,
    );
  }
  return value;
}

/** The main container of a component: constants, or its fields' values. */
function componentContainer(
  component: DescriptorComponent,
  values: FieldValues,
): ContainerResources {
  const figures: Partial<ContainerResources> = {};
  for (const key of RESOURCE_KEYS) {
    const ref = component.resources[key];
    if (ref === undefined) continue;
    const value = typeof ref === 'number' ? ref : valueOf(values, ref);
    if (typeof value !== 'number') {
      throw new Error(
        `Quota : ${component.id}.resources.${key} = ${JSON.stringify(
          value,
        )}, attendu un nombre.`,
      );
    }
    figures[key] = value;
  }
  return containerOf(figures, DEFAULT_CONTAINER);
}

function initContainers(
  spec: ExpressionNode | InitContainer[] | undefined,
  main: ContainerResources,
  values: FieldValues,
  where: string,
): PodResources[] {
  if (spec === undefined) {
    return [];
  }
  if (Array.isArray(spec)) {
    return spec.map(item => {
      if (item.kind === 'defaults') return CONTAINER_DEFAULTS;
      if (item.kind === 'self') return toPod(main);
      return toPod(containerOf(item.resources, DEFAULT_CONTAINER));
    });
  }
  return Array.from(
    { length: count(spec, values, `${where}.initContainers`) },
    () => CONTAINER_DEFAULTS,
  );
}

/** The sidecars of a quota row, as their pod's scheduler counts them. */
function sidecars(spec: SidecarContainer[] | undefined): PodResources[] {
  return (spec ?? []).map(item =>
    item.kind === 'defaults'
      ? CONTAINER_DEFAULTS
      : toPod(containerOf(item.resources, DEFAULT_CONTAINER)),
  );
}

function row(
  id: string,
  label: string,
  quota: QuotaRowSpec,
  defaultPods: ExpressionNode,
  main: ContainerResources,
  values: FieldValues,
): QuotaComponent {
  return {
    label: quota.label ?? label,
    pods: count(quota.pods ?? defaultPods, values, `${id}.pods`),
    pod: podOf(
      [toPod(main), ...sidecars(quota.sidecars)],
      initContainers(quota.initContainers, main, values, id),
    ),
    why: quota.why ? renderTemplate(quota.why, { values }) : '',
  };
}

function extraContainer(
  extra: DescriptorQuotaExtra,
  inputs: QuotaInputs,
): ContainerResources {
  return containerOf(
    hasOwn(inputs.extras, extra.id) ? inputs.extras[extra.id] : extra.resources,
    DEFAULT_CONTAINER,
  );
}

/** The quota of an environment of `profile` (header). */
export function computeQuotaWithDescriptor(
  descriptor: AppTypeDescriptor,
  input: QuotaInput,
  profile: QuotaProfile,
): EnvironmentQuota {
  const values: FieldValues = {
    ...input.inputs.values,
    ...input.configuration,
    ...(profile === 'ephemeral' ? descriptor.lightweight : {}),
  };
  const components: QuotaComponent[] = [];
  for (const entry of descriptor.quotaOrder) {
    if (entry.kind === 'component') {
      const component = entry.item;
      if (
        component.enabled &&
        !truthy(evaluate(component.enabled, { values }))
      ) {
        continue;
      }
      components.push(
        row(
          component.id,
          component.label,
          component.quota,
          component.pods,
          componentContainer(component, values),
          values,
        ),
      );
      continue;
    }
    const extra = entry.item;
    if (extra.scope !== 'both' && extra.scope !== profile) {
      continue;
    }
    if (extra.when && !truthy(evaluate(extra.when, { values }))) {
      continue;
    }
    components.push(
      row(
        extra.id,
        extra.label ?? extra.id,
        extra.quota,
        extra.pods,
        extraContainer(extra, input.inputs),
        values,
      ),
    );
  }
  // A feature off for this environment (`when`, Wagtail's uploads with its
  // media on S3: the appenv renders no XUploadS3ToApp) runs nothing.
  const runs = WORKFLOW_RUNS.filter(run =>
    featureEnabled(descriptor, run.feature, values),
  );
  if (profile === 'permanent' && runs.length > 0) {
    components.push({
      label: 'Workflows',
      pods: runs.reduce((total, run) => total + run.pods, 0),
      // main; Argo's wait and init: no resources.
      pod: podOf([WORKFLOW_MAIN_COUNTED, undefined], [undefined]),
      why: runs
        .map(run => (run.pods > 1 ? `${run.label} (${run.pods})` : run.label))
        .join(' + '),
    });
  }

  const sum = (figure: (component: QuotaComponent) => number) =>
    components.reduce(
      (total, component) => total + component.pods * figure(component),
      0,
    );
  const total = {
    pods: sum(() => 1),
    requests: {
      cpu: sum(component => component.pod.requests.cpu),
      memory: sum(component => component.pod.requests.memory),
    },
    limits: {
      cpu: sum(component => component.pod.limits.cpu),
      memory: sum(component => component.pod.limits.memory),
    },
    persistentVolumeClaims: count(
      descriptor.volumes.count,
      values,
      'volumes.count',
    ),
    storage: count(descriptor.volumes.storage, values, 'volumes.storage'),
  };
  return {
    profile,
    components,
    total,
    hard: {
      pods: withMargin(total.pods, 1),
      requests: {
        cpu: withMargin(total.requests.cpu, 100),
        memory: withMargin(total.requests.memory, 128),
      },
      limits: {
        cpu: withMargin(total.limits.cpu, 100),
        memory: withMargin(total.limits.memory, 128),
      },
      persistentVolumeClaims: withMargin(total.persistentVolumeClaims, 1),
      storage: withMargin(total.storage, 1),
    },
  };
}

/**
 * Why the environment would be refused, in French: its pods could never all
 * be scheduled, even on the largest cluster. Empty when it fits.
 */
export function quotaProblems(quota: EnvironmentQuota): string[] {
  const problems: string[] = [];
  const { requests } = quota.total;
  if (
    requests.cpu > CLUSTER_REQUESTS_MAX.cpu ||
    requests.memory > CLUSTER_REQUESTS_MAX.memory
  ) {
    problems.push(
      `À son maximum, l’environnement réserverait ${requests.cpu} m de CPU et ${requests.memory} Mio de mémoire, au-delà de ce que le cluster peut offrir (${CLUSTER_MAX_NODES} nœuds : ${CLUSTER_REQUESTS_MAX.cpu} m, ${CLUSTER_REQUESTS_MAX.memory} Mio). Réduire les pods maximum, les instances PostgreSQL ou les réservations.`,
    );
  }
  return problems;
}

const pad = (text: string, width: number) =>
  text.length >= width ? text : text + ' '.repeat(width - text.length);

/** `spec.hard` of `quota.yaml`, as strings. */
export function hardEntries(hard: QuotaHard): Record<string, string> {
  return {
    pods: String(hard.pods),
    'requests.cpu': `${hard.requests.cpu}m`,
    'requests.memory': `${hard.requests.memory}Mi`,
    'limits.cpu': `${hard.limits.cpu}m`,
    'limits.memory': `${hard.limits.memory}Mi`,
    persistentvolumeclaims: String(hard.persistentVolumeClaims),
    'requests.storage': `${hard.storage}Gi`,
  };
}

/** Every figure the larger of the two. */
export function maxHard(a: QuotaHard, b: QuotaHard): QuotaHard {
  return {
    pods: Math.max(a.pods, b.pods),
    requests: maxOf(a.requests, b.requests),
    limits: maxOf(a.limits, b.limits),
    persistentVolumeClaims: Math.max(
      a.persistentVolumeClaims,
      b.persistentVolumeClaims,
    ),
    storage: Math.max(a.storage, b.storage),
  };
}

export function sameHard(a: QuotaHard, b: QuotaHard): boolean {
  return JSON.stringify(hardEntries(a)) === JSON.stringify(hardEntries(b));
}

/**
 * The hard figures of a ResourceQuota's `spec.hard` (of `quota.yaml`, or of
 * the live object, whose quantities the API server may have rewritten:
 * `2000m` reads `2`), or undefined when one is missing or unreadable.
 */
export function readHard(hard: unknown): QuotaHard | undefined {
  const entries = record(hard);
  const whole = (key: string) => {
    const value = Number(entries?.[key]);
    return Number.isInteger(value) && value >= 0 ? value : undefined;
  };
  const figures = [
    whole('pods'),
    quantityIn(entries?.['requests.cpu'], 'm'),
    quantityIn(entries?.['requests.memory'], 'Mi'),
    quantityIn(entries?.['limits.cpu'], 'm'),
    quantityIn(entries?.['limits.memory'], 'Mi'),
    whole('persistentvolumeclaims'),
    quantityIn(entries?.['requests.storage'], 'Gi'),
  ];
  const [pods, rc, rm, lc, lm, pvcs, storage] = figures;
  if (
    pods === undefined ||
    rc === undefined ||
    rm === undefined ||
    lc === undefined ||
    lm === undefined ||
    pvcs === undefined ||
    storage === undefined
  ) {
    return undefined;
  }
  return {
    pods,
    requests: { cpu: rc, memory: rm },
    limits: { cpu: lc, memory: lm },
    persistentVolumeClaims: pvcs,
    storage,
  };
}

/** The hard figures of a `quota.yaml`'s content, or undefined. */
export function readQuotaFileHard(content: string): QuotaHard | undefined {
  const doc = parseDocument(content);
  if (doc.errors.length > 0) {
    return undefined;
  }
  return readHard(record(record(doc.toJS())?.spec)?.hard);
}

/**
 * `quota.yaml`: the ResourceQuota, its computation in comments. Without a
 * namespace: the overlay sets it (`replacements` of `<env>/kustomization.yaml`),
 * or the ephemeral ApplicationSet (`applicationset-ephemeral.yaml`).
 *
 * `transitionalHard`: the figures written instead of the computed ones while
 * a change of configuration is applied (`waadoo:env:configure`: the larger
 * of the old and the new quota), said in the header.
 */
export function renderQuotaManifest(
  quota: EnvironmentQuota,
  transitionalHard?: QuotaHard,
): string {
  const { total } = quota;
  const hard = transitionalHard ?? quota.hard;
  const rows = quota.components.map(
    component =>
      `#   ${pad(component.label, 13)} ${pad(
        `${component.pods} x ${component.pod.requests.cpu}m/${component.pod.requests.memory}Mi`,
        20,
      )} limites ${pad(
        `${component.pod.limits.cpu}m/${component.pod.limits.memory}Mi`,
        14,
      )} ${component.why}`,
  );
  return [
    quota.profile === 'permanent'
      ? "# QUOTA DU NAMESPACE de l'environnement, GENERE par Backstage a partir de"
      : '# QUOTA DU NAMESPACE de chaque branche ephemere, GENERE par Backstage a',
    quota.profile === 'permanent'
      ? '# values.yaml du meme dossier : waadoo:env:quota a la creation du projet,'
      : '# partir de dev/values.yaml sous le profil leger (values.yaml de ce',
    quota.profile === 'permanent'
      ? '# waadoo:env:configure a chaque changement (onglet Configuration). Ne pas'
      : '# dossier) : waadoo:env:quota a la creation, waadoo:env:configure a chaque',
    quota.profile === 'permanent'
      ? '# le modifier a la main : il serait reecrit.'
      : '# changement de dev. Ne pas le modifier a la main : il serait reecrit.',
    '#',
    '# Tout ce que le namespace peut faire tourner EN MEME TEMPS, a la',
    `# configuration maximale, plus ${QUOTA_MARGIN_PERCENT} % (calcul : environmentQuota.ts de`,
    '# Backstage). Par pod, comme le compte le scheduler ; les conteneurs sans',
    '# ressources declarees recoivent celles du LimitRange (base/limitrange.yaml).',
    ...rows,
    `# Total : ${total.pods} pods, reservations ${total.requests.cpu}m/${total.requests.memory}Mi, limites ${total.limits.cpu}m/${total.limits.memory}Mi,`,
    `# ${total.persistentVolumeClaims} volumes, ${total.storage} Gi.`,
    '#',
    ...(transitionalHard
      ? [
          '#',
          "# TRANSITOIRE : plafonds = le plus grand de l'ancien et du nouveau quota,",
          "# le temps d'appliquer un changement de configuration ; remplaces par",
          "# le calcul ci-dessus une fois l'environnement redeploye.",
        ]
      : []),
    '#',
    '# sync-wave -1 : avant les charges de travail (vague 0), apres le',
    "# namespace et le LimitRange (-2). Hors de portee des editeurs : l'AppProject",
    '# ne leur ouvre ni ResourceQuota ni LimitRange.',
    'apiVersion: v1',
    'kind: ResourceQuota',
    'metadata:',
    `  name: ${QUOTA_NAME}`,
    '  annotations:',
    '    argocd.argoproj.io/sync-wave: "-1"',
    'spec:',
    '  hard:',
    ...Object.entries(hardEntries(hard)).map(([key, value]) =>
      /^[0-9]+$/.test(value)
        ? `    ${key}: "${value}"`
        : `    ${key}: ${value}`,
    ),
    '',
  ].join('\n');
}
